import React, { useEffect, useState } from "react";
import { CeoNav } from "../components/CeoNav";
import { api, fmtNGN, formatErrorDetail } from "../lib/api";
import { CardSkeleton, EmptyState, ErrorState } from "../components/LoadState";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";

const empty = { name: "", size: "", crates_per_pallet: 50, rate_per_crate: 50 };

export default function ProductsPage() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // product object or null
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/products");
      setItems(data);
    } catch (e) {
      const msg = e.userMessage || "Failed to load products.";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async (form) => {
    try {
      if (editing?.id) {
        await api.patch(`/products/${editing.id}`, form);
        toast.success("Product updated");
      } else {
        await api.post("/products", form);
        toast.success("Product created");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.userMessage || formatErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await api.delete(`/products/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e.userMessage || "Failed to delete");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="products-page">
      <CeoNav />
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-display text-3xl font-bold">Products</h1>
            <div className="text-sm text-muted-foreground">Drinks, crate sizes and rates</div>
          </div>
          <button
            data-testid="product-new-btn"
            onClick={() => { setEditing({ ...empty }); setOpen(true); }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:brightness-110 font-semibold text-sm"
          >
            <Plus size={16} /> New product
          </button>
        </div>

        {error ? <div className="mb-5"><ErrorState message={error} onRetry={load} /></div> : null}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {loading ? <CardSkeleton rows={3} /> : null}
          {!loading && items.length === 0 ? (
            <div className="md:col-span-2 lg:col-span-3">
              <EmptyState title="No products yet" message="Create a product to start recording entries." />
            </div>
          ) : null}
          {!loading && items.map((p) => (
            <div key={p.id} className="card-soft p-5" data-testid={`product-${p.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-display text-xl font-bold">{p.name}</div>
                  <div className="text-xs text-muted-foreground">{p.size}</div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => { setEditing(p); setOpen(true); }} className="p-2 rounded-lg hover:bg-secondary" data-testid={`product-edit-${p.id}`}>
                    <Pencil size={14} />
                  </button>
                  <button onClick={() => remove(p.id)} className="p-2 rounded-lg hover:bg-destructive/10 text-destructive" data-testid={`product-del-${p.id}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3">
                <div className="p-3 rounded-lg bg-slate-50 border border-border">
                  <div className="text-xs text-muted-foreground">Crates/pallet</div>
                  <div className="font-semibold">{p.crates_per_pallet}</div>
                </div>
                <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-100">
                  <div className="text-xs text-emerald-700">Rate/crate</div>
                  <div className="font-semibold text-emerald-700">{fmtNGN(p.rate_per_crate)}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </main>

      <ProductDialog open={open} onClose={() => setOpen(false)} value={editing || empty} onSave={save} />
    </div>
  );
}

function ProductDialog({ open, onClose, value, onSave }) {
  const [f, setF] = useState(value);
  useEffect(() => { setF(value); }, [value]);
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="product-dialog">
        <DialogTitle>{f?.id ? "Edit product" : "New product"}</DialogTitle>
        <div className="space-y-3 mt-3">
          <Field label="Name" value={f.name} onChange={(v) => setF({ ...f, name: v })} testId="product-name" />
          <Field label="Size (e.g. 50CL)" value={f.size} onChange={(v) => setF({ ...f, size: v })} testId="product-size" />
          <Field label="Crates per pallet" type="number" value={f.crates_per_pallet} onChange={(v) => setF({ ...f, crates_per_pallet: Number(v) })} testId="product-cpp" />
          <Field label="Rate per crate (NGN)" type="number" value={f.rate_per_crate} onChange={(v) => setF({ ...f, rate_per_crate: Number(v) })} testId="product-rate" />
        </div>
        <div className="mt-5 flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-border hover:bg-secondary text-sm font-semibold">Cancel</button>
          <button onClick={() => onSave(f)} data-testid="product-save" className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:brightness-110">Save</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, onChange, type = "text", testId }) {
  return (
    <div>
      <label className="text-sm font-semibold mb-1.5 block">{label}</label>
      <input
        data-testid={testId}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2.5 rounded-lg border border-input bg-white focus:outline-none focus:ring-2 focus:ring-primary/40"
      />
    </div>
  );
}
